"use client";

import React, { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { cva, VariantProps } from "class-variance-authority";
import Link from "next/link";
import Image from "next/image";
import { Button } from "../ui/Button";
import { MenuItem } from "@/types/common";

import { usePathname } from "next/navigation";
import searchIcon from "@/assets/search.svg";
import { MainMenus } from "@/types/common";
// import { Menu as MenuIcon } from "lucide-react";
import { useState } from "react";
// import { sessionOptions } from "@/pages/api/getAccessToken";
// import { getIronSession } from "iron-session";
// import { useRouter } from "next/navigation";
import SearchModal from "./SearchModal";
import CountrySelector from "../ui/CountrySelector";
import { ChevronDown, Star } from "lucide-react";
// import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext"; // ✅ adjust path if needed
import DropdownMenu from "../ui/Dropdown";
import GumletImage from "@/utils/GumletSrcSet";

const headerVariants = cva("top-0 z-50 w-full bg-indigo border-gray-800", {
  variants: {
    size: {
      sm: "px-4 lg:px-10",
      md: "px-4 lg:px-8",
      lg: "px-4 lg:px-40",
    },
  },
  defaultVariants: { size: "md" },
});

const navVariants = cva("text-sm font-medium", {
  variants: {
    active: {
      true: "text-orange underline",
      false: "text-background",
    },
  },
  defaultVariants: { active: false },
});

const isMenuActive = (itemHref: string, pathname: string) => {
  // Remove trailing slashes except for root
  const clean = (s: string) => s === "/" ? "/" : s.replace(/\/+$/, "");
  const href = clean(itemHref || "");
  const path = clean(pathname || "");

  // Special case: don't highlight /events for /event/... and vice versa
  if ((href === "/events" && path.startsWith("/event/")) ||
    (href === "/event" && path.startsWith("/events/"))) return false;

  // Highlight if path matches or is a child of the menu item
  return path === href || path.startsWith(href + "/");
};

export interface HeaderProps extends VariantProps<typeof headerVariants> {
  className?: string;
  items: MainMenus;
  hostname?: string;
}

const MainHeader = ({ items, size, className, hostname }: HeaderProps) => {
  const fetchCount = useRef(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [menuMobileDropdown, setMenuMobileDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [researchDropdownOpen, setResearchDropdownOpen] = useState(false);

  const { sessionUser, fetchSession, logout, sessionLoading } = useAuth();

  const pathname = usePathname();


  useEffect(() => {
    const loadSession = async () => {
      if (
        !sessionUser?.subscriber_id &&
        !sessionLoading &&
        fetchCount.current < 2
      ) {
        fetchCount.current += 1;
        await fetchSession();
      }
    };

    loadSession();
  }, [sessionUser?.subscriber_id, sessionLoading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
      }
    };

    const handleScroll = () => {
      setOpenDropdown(false);
    };
    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, []);


  const logoutHandler = async () => {
    await logout();
    setMenuMobileDropdown(false);
    setOpenDropdown(false);
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;

      // Prevent scroll on the body
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }

    return () => {
      // Clean up
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [isMobileMenuOpen]);

  // const sections = [
  //   { key: "HeaderLeft", className: "text-background text-xl font-medium" },
  //   { key: "HeaderRight", className: "text-background text-xl font-normal" },
  //   {
  //     key: "FooterCol1",
  //     className: "text-background/60 text-base font-normal font-inter",
  //   },
  // ];

  // const researchMenuItems = [
  //   { label: "SHRPA", href: "/research/shrpa" },
  //   {
  //     label: "People Matters Initiative",
  //     href: "/research/people-matters-initiative",
  //   },
  //   { label: "Co Branded Initiative", href: "/research/co-branded-initiative" },
  // ];

  const researchItem = items.HeaderRight?.find(
    (item) => item.href === "/research"
  );
  
  const researchMenuItems = researchItem?.children?.map((item) => ({
    label: item.label,
    href: item.href || "#",
  }));


  return (
    <header className={twMerge(headerVariants({ size }), className)}>
      <div
        className="flex flex-col max-w-7xl mx-auto px-0 lg:px-0"
      >
        <nav className="flex justify-between lg:py-0 py-6 items-center w-full">
          <Link
            href="/"
            className="flex items-center lg:pt-4 lg:pb-2"
            prefetch={false}
          >
              <div className="w-[218px] lg:w-[600px] aspect-[600/100] relative">
                <GumletImage
                  imageUrl={`${process.env.NEXT_PUBLIC_ASSET_URL}/images/63b86e5a-0cb3-4f30-a382-b2c70ed7a34a.png` || "/assets/logo.png"}
                  fallbackSrc="/assets/images/dummy.jpg"
                  alt="People Matters Logo"
                  fill
                  className="object-contain"
                  fetchPriority="high"
                  sizes="(max-width:1024px) 218px, 600px"
                />
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-4">
            <CountrySelector hostname={hostname} />
            <ul className="hidden lg:flex justify-between items-center flex-row text-background">
              {items?.UpperNav?.map((item: MenuItem, index: number) => (
                <li key={`UpperNav-${index}`}>
                  {item.label === "Subscribe" ? (
                    <Button
                      className="hidden"
                      key={item.href}
                      capitalize={true}
                      size="sm"
                    >
                      {item.label}
                    </Button>
                  ) : (
                    <>
                      {((item as any).href === "/auth/login" || (item as any).href === "/auth/signup") &&
                        (sessionUser as any)?.user_email &&
                        (sessionUser as any)?.subscriber_id ? (
                        <div className="relative">
                          {" "}
                          <button
                            aria-label="Toggle user menu"
                            onClick={() => setOpenDropdown(!openDropdown)}
                            className="flex items-center gap-1 text-background text-sm font-medium transition-all duration-300"
                          >
                            <span className="bg-orange p-1 rounded-full">
                              <Star color="#000" size={16} />
                            </span>
                            {/* Logout ( */}
                            {(sessionUser as any)?.first_name ||
                              (sessionUser as any)?.last_name
                              ? `${(sessionUser as any)?.first_name || ""} ${(sessionUser as any)?.last_name || ""
                                }`.trim()
                              : (sessionUser as any)?.user_email || "User"}
                            {/* ) */} <ChevronDown size={20} />
                          </button>
                          {openDropdown && (
                            <div ref={dropdownRef} className="absolute right-0 mt-2 w-52 rounded-xl shadow-lg bg-white ring-1 ring-black/5 z-50">
                              <div className="relative">
                                {/* Arrow */}
                                <div className="absolute top-0 right-5 w-6 h-6 bg-white rounded-md rotate-45 -mt-2 z-[-1]" />

                                {/* Menu items */}
                                <ul className="py-4 px-4 text-black text-[16px] font-medium">
                                  <li>
                                    <Link onClick={(e) => setOpenDropdown(false)} className={`${pathname == "/my-account/profile" ? "text-orange" : ""} hover:text-orange px-4 py-2 inline-flex w-full`} href={"/my-account/profile"} prefetch={false}>
                                      My Profile
                                    </Link>{" "}
                                  </li>
                                  {/* <li className="cursor-pointer hover:text-orange">
                                    <Link onClick={() => setOpenDropdown(false)} className={`${pathname == "/my-account/subscriptions" ? "text-orange" : ""}`} href={"/my-account/subscriptions"} prefetch={false}>
                                      Subscription
                                    </Link>
                                  </li> */}
                                  <li>
                                    <Link onClick={() => setOpenDropdown(false)} className={`${pathname == "/my-account/saved" ? "text-orange " : ""} hover:text-orange px-4 py-2 inline-flex w-full`} href={"/my-account/saved"} prefetch={false}>
                                      Saved
                                    </Link>
                                  </li>
                                  <li>
                                    <Link onClick={() => setOpenDropdown(false)} className={`${pathname == "/my-account/events" ? "text-orange " : ""} hover:text-orange px-4 py-2 inline-flex w-full`} href={"/my-account/events"} prefetch={false}>
                                      Events
                                    </Link>
                                  </li>
                                  <li className="px-4 cursor-pointer py-2 hover:text-orange">
                                    <span onClick={logoutHandler}>Logout</span>{" "}
                                  </li>
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link
                          key={item.href}
                          href={item.href || "#"}
                          className={twMerge(
                            navVariants({ active: isMenuActive(item?.href || "", pathname || "") }),
                            " transition-all duration-300"
                          )}
                          prefetch={false}
                        >
                          {item.label}
                        </Link>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
          {/* Mobile/Tablet Controls */}
          <div className="flex items-center gap-4 lg:hidden">
            <button
              aria-label="Open search"
              className="text-background"
              onClick={() => setIsSearchModalOpen(true)}
            >
              <Image
                src={searchIcon}
                alt="Search"
                width={19}
                height={19}
                style={{ width: "19px", height: "19px" }}
              />
            </button>
            <button
              aria-label={isMobileMenuOpen ? "Close mobile menu" : "Open mobile menu"}
              className="text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <Image
                src="/assets/images/menu.svg"
                className="text-white"
                alt="Menu"
                width={28}
                height={21}
                style={{ width: "28px", height: "21px" }}
              />
              {/* <MenuIcon size={24} /> */}
            </button>
          </div>
        </nav>

        {/* Desktop Navigation */}
        <div className="hidden lg:block">
          <hr className="border-background height-[0.2px] opacity-20 pt-[10px] pb-5" />
          <nav className="flex pb-8 justify-between">
            <ul className="flex items-center xl:space-x-10 space-x-4">
              {(items?.HeaderLeft ?? []).map((item, index) => (
                <li key={`HeaderLeft-${index}`}>
                  <Link
                    key={item.href}
                    href={item.href || "#"}
                    className={twMerge(
                      navVariants({ active: isMenuActive(item?.href || "", pathname || "") }),
                      " transition-all duration-300"
                    )}
                    prefetch={false}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}

              <li>
                <button
                  type="button"
                  aria-label="Open search"
                  onClick={() => setIsSearchModalOpen(true)}
                  className="flex items-center"
                >
                  <Image
                    src={searchIcon}
                    alt=""
                    width={19}
                    height={19}
                    style={{ width: "19px", height: "19px" }}
                  />
                </button>
              </li>
            </ul>
            <ul className="hidden items-center xl:space-x-10 space-x-4 md:flex">
              {(items?.HeaderRight ?? []).map((item, index) => (
                <li
                  key={`HeaderRight-${index}`}
                  onMouseEnter={() => {
                    if (item.label === "Research" && researchItem?.children && researchItem.children.length > 0)
                      setResearchDropdownOpen(true);
                  }}
                  onMouseLeave={() => {
                    if (item.label === "Research" && researchItem?.children && researchItem.children.length > 0)
                      setResearchDropdownOpen(false);
                  }}
                  className="relative"
                >
                  <Link
                    key={item.href}
                    href={item.href || "#"}
                    className={twMerge(
                      navVariants({ active: isMenuActive(item?.href || "", pathname || "") }),
                      " transition-all duration-300"
                    )}
                    prefetch={false}
                  >
                    {item.label}
                  </Link>
                  {item.label === "Research" && researchItem?.children && researchItem.children.length > 0 && (
                    <DropdownMenu
                      open={researchDropdownOpen}
                      onClose={() => setResearchDropdownOpen(false)}
                      pathname={pathname || ""}
                      menuItems={researchMenuItems}
                    />
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Mobile Menu Overlay */}
        <div
          className={`
          fixed inset-0 bg-indigo z-50 transition-transform duration-300 ease-in-out p-6
          ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}
          lg:hidden   
        `}
        >
          <div className="flex flex-col h-full p-4 overflow-y-auto">
            <div className="flex justify-between items-center mb-8">
              <Link href="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center space-x-2" prefetch={false}>
                <GumletImage
                  imageUrl={`${process.env.NEXT_PUBLIC_ASSET_URL}/images/9b10de69-1f08-4fe6-89eb-0bbdc0591cd7.png`}
                  alt="People Matters Logo"
                  width={70}
                  height={20}
                  className="w-[70px] md:w-[100px] lg:w-[120px]"
                  fetchPriority="high"
                />
              </Link>
              <div className="flex items-center space-x-4">
                <Image
                  src={searchIcon}
                  alt="Search"
                  width={19}
                  className="cursor-pointer"
                  height={19}
                  onClick={() => setIsSearchModalOpen(true)}
                />
                <button
                  aria-label="Close mobile menu"
                  className="text-background p-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Mobile Menu Items */}
            <div className="flex flex-col space-y-6">
              {/* {JSON.stringify(items)} */}
              {/* Show only items with href="auth/login" */}
              <div>
                <ul className="flex items-center justify-between">
                  {(items?.UpperNav ?? []).filter(
                    (item) => item.label != "Subscribe"
                  ).map((item, index) => (
                    <li key={`UpperNav-${index}`}>
                      <div>
                        {(item as any).label === "Login / Signup" &&
                          (sessionUser as any)?.user_email &&
                          (sessionUser as any)?.subscriber_id ? (
                          // (sessionUser as any)?.token ? (
                          <div className="relative">
                            {" "}
                            <button
                              aria-label="Toggle user menu"
                              onClick={() =>
                                setMenuMobileDropdown(!menuMobileDropdown)
                              }
                              className="flex items-center gap-1 text-background text-sm font-medium transition-all duration-300"
                            >
                              <span className="bg-orange p-1 rounded-full">
                                <Star color="#000" size={16} />
                              </span>
                              {(sessionUser as any)?.first_name ||
                                (sessionUser as any)?.last_name
                                ? `${(sessionUser as any)?.first_name || ""} ${(sessionUser as any)?.last_name || ""
                                  }`.trim()
                                : (sessionUser as any)?.user_email || "User"}
                            </button>
                          </div>
                        ) : (
                          <Link
                            key={item.href}
                            href={item.href || "#"}
                            className={twMerge(
                              navVariants({ active: isMenuActive(item?.href || "", pathname || "") }),
                              " transition-all duration-300 text-xl"
                            )}
                            prefetch={false}
                          >
                            {item.label}
                          </Link>
                        )}
                      </div>
                    </li>
                  ))}
                  <CountrySelector hostname={hostname} />
                </ul>

                {(sessionUser as any)?.user_email &&
                  (sessionUser as any)?.subscriber_id && (
                    <ul className="pt-2 text-[16px] text-white font-medium">
                      <li className="cursor-pointer py-2 text-base opacity-60">
                        <Link
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`${pathname == "/my-account/profile"
                            ? "text-orange"
                            : ""
                            }`}
                          href={"/my-account/profile"}
                          prefetch={false}
                        >
                          My Profile
                        </Link>{" "}
                      </li>
                      {/* <li className="cursor-pointer py-2 text-base opacity-60">
                        <Link
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`${
                            pathname == "/my-account/subscriptions"
                              ? "text-orange"
                              : ""
                          }`}
                          href={"/my-account/subscriptions"}
                          prefetch={false}
                        >
                          Subscription
                        </Link>
                      </li> */}
                      <li className="cursor-pointer py-2 text-base opacity-60">
                        <Link
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`${pathname == "/my-account/saved" ? "text-orange" : ""
                            }`}
                          href={"/my-account/saved"}
                          prefetch={false}
                        >
                          Saved
                        </Link>
                      </li>
                      <li className="cursor-pointer py-2 text-base opacity-60">
                        <Link
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`${pathname == "/my-account/events"
                            ? "text-orange"
                            : ""
                            }`}
                          href={"/my-account/events"}
                          prefetch={false}
                        >
                          Events
                        </Link>
                      </li>
                    </ul>
                  )}
              </div>
              <hr className="border-background opacity-80 my-4" />

              <ul className="flex flex-col gap-8">
                {(items?.HeaderLeft ?? []).map((item, index) => (
                  <li key={`HeaderLeft-${index}`}>
                    <Link
                      key={item.href}
                      href={item.href || "#"}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={twMerge(
                        navVariants({ active: isMenuActive(item?.href || "", pathname || "") }),
                        " transition-all duration-300 text-[20px] font-normal"
                      )}
                      prefetch={false}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <hr className="border-background opacity-80 my-4" />

              <ul className="flex flex-col gap-8">
                {(items?.HeaderRight ?? []).map((item, index) => (
                  <li key={`HeaderRight-${index}`}>
                    <Link
                      key={item.href}
                      href={item.href || "#"}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={twMerge(
                        navVariants({ active: isMenuActive(item?.href || "", pathname || "") }),
                        " transition-all duration-300 text-[20px] font-normal"
                      )}
                      prefetch={false}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="pb-20">
                <hr className="border-background opacity-80 my-4" />
                <div className="flex flex-col items-start gap-2 my-6">
                  <span className="text-background text-base font-normal font-inter">
                    Find us:
                  </span>
                  <div className="flex items-center justify-between w-full">
                    {(items?.SocialMenu ?? []).map((item, index) => (
                      <Link
                        key={`SocialMenu-${index}`}
                        href={item.href || "#"}
                        target="_blank"
                        className="text-gray-300 hover:text-white transition-colors relative items-center"
                        prefetch={false}
                      >
                        {item.icon ? (
                          <div className="flex items-center justify-center h-6 w-6">
                            <GumletImage
                              imageUrl={item.icon}
                              fallbackSrc="https://placehold.co/100x100"
                              alt={item.label}
                              fill
                              className="object-contain"
                            />
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            {item.label}
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
                <hr className="border-background opacity-80" />
                {(sessionUser as any)?.user_email &&
                  (sessionUser as any)?.subscriber_id && (
                    <span
                      onClick={logoutHandler}
                      className="uppercase block text-center text-orange text-base font-normal pt-8"
                    >
                      Logout
                    </span>
                  )}
              </div>

              {/* if title is subcribe then stick the button to the bottom of the menu */}
              {/* stick the button to the bottom of the menu */}
              {/* {items.UpperNav.map(
                (item, index) =>
                  item.label === "Subscribe" && (
                    <Button
                      key={item.href}
                      borderVariant="none"
                      className="w-full bg-orange text-white text-lg font-medium py-4 px-4 fixed bottom-0 left-0 right-0"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Button>
                  )
              )} */}
            </div>
          </div>
        </div>
      </div>
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
    </header>
  );
};

export default MainHeader;
